// CMS data client — all admin/content operations over PocketBase. Security is
// enforced server-side by the collection API rules + the role-change hook; these
// helpers are just the typed front door. Everything fails soft for guests.
import { pb } from "../lib/pocketbase.js";
import { isAuthed, currentUser } from "../lib/auth.js";

export function currentRole() {
  return (isAuthed() && currentUser() && currentUser().role) || "member";
}
export function isEditor() {
  const r = currentRole();
  return r === "editor" || r === "admin";
}
export function isAdmin() {
  return currentRole() === "admin";
}

// Resolve a PocketBase stored-file name to a served URL across SDK versions
// (getURL replaced getUrl around v0.21).
export function fileUrl(record, filename) {
  const f = pb.files && (pb.files.getURL || pb.files.getUrl);
  return f ? f.call(pb.files, record, filename) : "";
}

// --- Articles (editor/admin) ----------------------------------------------

// Editors see every status; the API rule lets them. Newest-edited first.
export async function listAllArticles() {
  if (!isEditor()) return [];
  try {
    return await pb.collection("grammar_articles").getFullList({ sort: "-updated" });
  } catch {
    return [];
  }
}

export async function getArticle(id) {
  if (!id) return null;
  try {
    return await pb.collection("grammar_articles").getOne(id);
  } catch {
    return null;
  }
}

// Create or update. `data` is a plain object of the editable fields.
export async function saveArticle(id, data) {
  if (!isEditor()) throw new Error("You don't have permission to edit content.");
  const payload = {
    title: (data.title || "").slice(0, 300),
    slug: (data.slug || "").trim(),
    content_markdown: data.content_markdown || "",
    status: data.status === "published" ? "published" : "draft",
    is_navigable: !!data.is_navigable,
    nav_group: data.nav_group || "",
    nav_order: Number(data.nav_order) || 0,
    meta_description: (data.meta_description || "").slice(0, 300),
    aliases: data.aliases || "",
    difficulty_level: data.difficulty_level || "",
    path_order: Number(data.path_order) || 0,
  };
  if (data.category) payload.category = data.category;
  return id
    ? pb.collection("grammar_articles").update(id, payload)
    : pb.collection("grammar_articles").create(payload);
}

export async function deleteArticle(id) {
  if (!isEditor() || !id) return;
  try {
    await pb.collection("grammar_articles").delete(id);
  } catch {
    /* best effort */
  }
}

// --- Media (inline image uploads) -----------------------------------------

// Upload one image blob/file to the media collection and return its public URL.
// Used by the editor's image hook.
export async function uploadMedia(blob, alt) {
  if (!isEditor()) throw new Error("You don't have permission to upload.");
  const fd = new FormData();
  fd.append("file", blob, blob.name || "image.png");
  if (alt) fd.append("alt", alt);
  const uid = currentUser() && currentUser().id;
  if (uid) fd.append("uploaded_by", uid);
  const rec = await pb.collection("media").create(fd);
  return fileUrl(rec, rec.file);
}

// Upload an image for a personal note — any signed-in user (not just editors).
// Goes to the same media collection; returns the served URL.
export async function uploadUserImage(blob, alt) {
  if (!isAuthed()) throw new Error("Sign in to add images.");
  const fd = new FormData();
  fd.append("file", blob, blob.name || "image.png");
  if (alt) fd.append("alt", alt);
  const uid = currentUser() && currentUser().id;
  if (uid) fd.append("uploaded_by", uid);
  const rec = await pb.collection("media").create(fd);
  return fileUrl(rec, rec.file);
}

// --- Navigable content (public) -------------------------------------------

// Published, navigable articles for the DB-driven nav section, in author order.
export async function fetchNavItems() {
  try {
    return await pb.collection("grammar_articles").getFullList({
      filter: 'is_navigable = true && status = "published"',
      sort: "nav_order",
      fields: "id,slug,title,nav_group,nav_order",
    });
  } catch {
    return [];
  }
}

// --- Users / roles (admin) -------------------------------------------------

export async function listUsers() {
  if (!isAdmin()) return [];
  try {
    return await pb.collection("users").getFullList({ sort: "email", fields: "id,email,name,role" });
  } catch {
    return [];
  }
}

// Allowed by the server only for admins (the role-change hook enforces it).
export async function setUserRole(id, role) {
  if (!isAdmin() || !id) throw new Error("Only an admin can change roles.");
  const r = role === "admin" || role === "editor" ? role : "member";
  return pb.collection("users").update(id, { role: r });
}
